import { useState, useCallback, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';

export interface LoanPortfolioStats {
  // Tier 1 - Primary Metrics (Revenue Context)
  lineOfTrail: number;           // Loans with commission (matches Dashboard LOT)

  // Tier 2 - Portfolio Metrics (Asset Context)
  undischargedLoans: number;     // Loans without discharge event
  dischargedLoans: number;       // Loans with discharge event

  // Tier 3 - Historical Metrics
  totalLoansEver: number;        // All loans ever (undischarged + discharged)

  // Age metrics
  avgActiveAgeMonths: number;    // Average age of undischarged loans
  avgDischargedAgeMonths: number; // Average tenure of discharged loans

  // Legacy aliases (for backward compatibility)
  totalLoans: number;            // Alias for totalLoansEver
  activeLoans: number;           // Alias for undischargedLoans
}

interface LoanPortfolioStatsRequest {
  companyId: string;
  officeIds?: string[];
  filters?: {
    brokerName?: string;
    lender?: string;
  };
  entityCode?: string;
}

export function useLoanPortfolioStats(filters?: { brokerName?: string; lender?: string }, entityCode?: string) {
  const { user, companyId } = useAuth();
  const [stats, setStats] = useState<LoanPortfolioStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!companyId || !user) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const getLoanPortfolioStatsFn = httpsCallable<LoanPortfolioStatsRequest, LoanPortfolioStats>(
        functions,
        'getLoanPortfolioStats'
      );

      // Let the backend resolveOfficeScope decide office scoping based on role.
      // Administrators/founders/super_admins see all offices; office_admins/brokers
      // are automatically scoped to their assigned offices server-side.
      // Sending officeIds from the frontend caused a mismatch: the frontend checked
      // only canSeeAllOffices (claim), while the backend also checks role.
      const response = await getLoanPortfolioStatsFn({
        companyId,
        filters,
        entityCode,
      });

      setStats(response.data);
    } catch (err: any) {
      console.error('Error fetching loan portfolio stats:', err);
      setError(err.message || 'Failed to fetch loan portfolio stats');
    } finally {
      setLoading(false);
    }
  }, [companyId, user, filters, entityCode]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const refresh = useCallback(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refresh,
  };
}
